// GA4 event helpers — safe no-ops when gtag is not loaded (dev / no consent).
// Follows Google's Recommended Events pattern for lead-gen / B2B sites.
//
// Design principle: **no hardcoded platform list**. New contact channels or
// social profiles configured in CMS SiteSettings are auto-tracked because
// the generic `trackChannelClick()` accepts any platform slug the CMS emits.
//
// Public API:
//   • trackEvent(name, params)             — generic escape hatch
//   • trackCtaClick(label, location)       — primary CTAs
//   • trackChannelClick(channel, location) — any contact / social channel
//   • trackGenerateLead(params)            — key conversion (form submit)

export function trackEvent(name, params = {}) {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  // GA4 limits: param key ≤40 chars, string value ≤100 chars. Truncate.
  const clean = {};
  for (const [k, v] of Object.entries(params)) {
    if (v == null) continue;
    const key = String(k).slice(0, 40);
    clean[key] = typeof v === "string" ? v.slice(0, 100) : v;
  }
  try {
    window.gtag("event", name, clean);
  } catch { /* never let analytics break UX */ }
}

/** Primary CTA click. Google Recommended pattern.
 * `location` example: "nav-header", "home-hero", "services-bottom". */
export function trackCtaClick(label, location, extra = {}) {
  trackEvent("cta_click", {
    cta_label: label || "",
    cta_location: location || "",
    ...extra,
  });
}

/**
 * Contact/social channel click — a *single* event whose `platform` param
 * carries the CMS-configured slug. Adding a new channel in Payload (e.g.
 * "telegram", "wechat", "signal") automatically flows through here — no
 * code change or deploy required.
 *
 * `channel` must be a channel object as emitted by /api/seo/site-metadata:
 *   { platform, type, url, display, ... }
 */
export function trackChannelClick(channel, location, extra = {}) {
  if (!channel) return;
  trackEvent("contact_channel_click", {
    platform: channel.platform || "unknown",
    channel_type: channel.type || "unknown",
    link_url: channel.url || "",
    link_location: location || "",
    ...extra,
  });
}

/** Successful lead — MUST be marked as Key Event / Conversion in GA4. */
export function trackGenerateLead(params = {}) {
  trackEvent("generate_lead", {
    currency: params.currency || "USD",
    value: params.value ?? 0,
    ...params,
  });
}
